// src/pages/CustomerReport/CustomerSynopsisReport.jsx
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import QrCode2Icon from '@mui/icons-material/QrCode2'; // [CASH-MODE] Cash QR shortcut
import { useNavigate } from 'react-router-dom'; // [CASH-MODE]
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
import { customerNoOf } from '../../components/CustomerReportShell';
import CustomerProfileDialog from '../../components/CustomerProfileDialog';

// Masking helpers: unmasked only with customers.synopsis.viewSensitive
const maskMobile = (mobile, canViewSensitive) => {
  if (!mobile || mobile === 'N/A') return 'N/A';
  if (canViewSensitive) return mobile;
  if (mobile.length <= 4) return mobile;
  return mobile.slice(0, 4) + '****' + mobile.slice(-2);
};

const maskVPA = (vpa, canViewSensitive) => {
  if (!vpa || vpa === 'N/A') return 'N/A';
  if (canViewSensitive) return vpa;
  const parts = vpa.split('@');
  if (parts.length < 2) return vpa.slice(0, 3) + '****';
  const name = parts[0];
  const domain = parts[1];
  const maskedName = name.length <= 3 ? name[0] + '***' : name.slice(0, 3) + '****';
  return `${maskedName}@${domain}`;
};

const CustomerSynopsisReport = () => {
  const { can } = usePermissions();
  const navigate = useNavigate(); // [CASH-MODE]
  const [searchInput, setSearchInput] = useState('');
  const [searchType, setSearchType] = useState('all'); // 'all' | 'mobile' | 'customerId' | 'vpa'

  // Table & Pagination state
  const [customerList, setCustomerList] = useState([]);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableLoading, setTableLoading] = useState(true);

  // Profile dialog (shared with Total Customers)
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [customer, setCustomer] = useState(null);

  const canViewSensitive = can('customers.synopsis.viewSensitive');
  const canCashQr = can('customers.cashQr');

  // Fetch paginated customer list
  const fetchCustomerList = useCallback(async () => {
    setTableLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage
      };
      if (searchInput.trim()) {
        params.search = searchInput.trim();
      }
      const res = await axios.get(`${API_BASE_URL}/customerProfiles`, { params });
      if (res.status === 200 && res.data) {
        const list = res.data.data || res.data.customers || res.data.customerProfiles || [];
        setCustomerList(list);
        setTotalCustomers(res.data.total || res.data.count || list.length);
      }
    } catch (err) {
      console.error('Error fetching customer list:', err);
    } finally {
      setTableLoading(false);
    }
  }, [page, rowsPerPage, searchInput]);

  useEffect(() => {
    fetchCustomerList();
  }, [fetchCustomerList]);

  const loadCustomerSynopsis = (foundCust) => {
    setCustomer(foundCust);
    setProfileModalOpen(true);
  };

  // Search Customer by Mobile, ID, or VPA
  const handleSearch = async () => {
    if (!searchInput.trim()) {
      fetchCustomerList();
      return;
    }
    setPage(0);
    fetchCustomerList();

    // Also attempt single profile match for top search
    const query = searchInput.trim();
    try {
      let res;
      if (searchType === 'mobile') {
        res = await axios.get(`${API_BASE_URL}/customerProfiles/searchByPhone`, { params: { phoneNumber: query } });
      } else if (searchType === 'customerId') {
        // Customer ID = public 10-digit Customer No (list search matches customerNo)
        res = await axios.get(`${API_BASE_URL}/customerProfiles`, { params: { search: query, limit: 10 } });
      } else if (searchType === 'vpa') {
        res = await axios.get(`${API_BASE_URL}/customerProfiles/searchByVPA`, { params: { vpa: query } });
      } else {
        try {
          res = await axios.get(`${API_BASE_URL}/customerProfiles/searchByPhone`, { params: { phoneNumber: query } });
        } catch (e) {
          try {
            res = await axios.get(`${API_BASE_URL}/customerProfiles/searchByVPA`, { params: { vpa: query } });
          } catch (e2) {
            res = await axios.get(`${API_BASE_URL}/customerProfiles`, { params: { search: query, limit: 10 } });
          }
        }
      }

      let foundCust = null;
      if (res && res.data) {
        if (res.data.customerProfile) foundCust = res.data.customerProfile;
        else if (res.data.customers && res.data.customers.length > 0) foundCust = res.data.customers[0];
        else if (res.data.data && res.data.data.length > 0) foundCust = res.data.data[0];
        else if (res.data.customerId || res.data._id) foundCust = res.data;
      }

      if (foundCust) {
        loadCustomerSynopsis(foundCust);
      }
    } catch (err) {
      console.error('Single search lookup:', err);
    }
  };

  return (
    <Box sx={{ p: 3, position: 'relative' }}>
      <BackButton />
      <br /><br />
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 3 }}>
        Customers Synopsis
      </Typography>

      {/* Search Bar */}
      <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="search-type-label">Search By</InputLabel>
              <Select
                labelId="search-type-label"
                value={searchType}
                label="Search By"
                onChange={(e) => setSearchType(e.target.value)}
              >
                <MenuItem value="all">Auto Detect (Mobile / ID / VPA)</MenuItem>
                <MenuItem value="mobile">Customer Mobile Number</MenuItem>
                <MenuItem value="customerId">Customer ID</MenuItem>
                <MenuItem value="vpa">Customer VPA ID</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Search Query"
              placeholder={
                searchType === 'mobile'
                  ? 'Enter Customer Mobile (e.g. 9847012345)'
                  : searchType === 'customerId'
                  ? 'Enter Customer ID (10 digits)'
                  : searchType === 'vpa'
                  ? 'Enter VPA ID (e.g. abhikayila1@oksbi)'
                  : 'Enter Mobile Number, Customer ID, or VPA ID'
              }
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={tableLoading}
              sx={{ height: 40, fontWeight: 700 }}
            >
              {tableLoading ? 'Searching...' : 'Search Profile'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Paginated Customer Synopsis Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
        {tableLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1e293b' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>SL No</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Customer ID</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Customer Name</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Customer Mobile Number</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Customer VPA ID</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Customer Constituency</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 700 }}>Customer Available Smart Points</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 700 }}>Action / Details</TableCell>
                  {canCashQr && <TableCell align="center" sx={{ color: 'white', fontWeight: 700 }}>Cash QR</TableCell>}{/* [CASH-MODE] */}
                </TableRow>
              </TableHead>
              <TableBody>
                {customerList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canCashQr ? 9 : 8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No customer profiles found matching search criteria.
                    </TableCell>
                  </TableRow>
                ) : (
                  customerList.map((row, idx) => {
                    const rowId = customerNoOf(row);
                    const rowConst = row.constituency || row.customerConstituency || row.customerTalukName || 'Thiruvananthapuram';
                    const rowVpas = Array.isArray(row.customerVPA)
                      ? row.customerVPA.map(v => typeof v === 'string' ? v : v.vpa || '').filter(Boolean)
                      : [];
                    const points = row.availableSmartPoints || row.smartPointsBalance || 0;

                    return (
                      <TableRow key={row._id} hover sx={{ '&:hover': { backgroundColor: '#f8fafc' } }}>
                        <TableCell sx={{ fontWeight: 600 }}>{page * rowsPerPage + idx + 1}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'primary.main' }}>
                          {rowId}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{row.customerName || 'N/A'}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {maskMobile(row.customerMobile, canViewSensitive)}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {rowVpas.length > 0 ? (
                              rowVpas.map((v, vIdx) => (
                                <Chip key={vIdx} label={maskVPA(v, canViewSensitive)} size="small" variant="outlined" color="primary" />
                              ))
                            ) : (
                              <Typography variant="caption" color="text.secondary">N/A</Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                          {rowConst}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 800, color: '#0284c7' }}>
                          {points.toLocaleString('en-IN')} Pts
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Full Customer Profile Synopsis">
                            <IconButton color="primary" onClick={() => loadCustomerSynopsis(row)}>
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        {/* [CASH-MODE] open the customer's cash payment QR */}
                        {canCashQr && (
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<QrCode2Icon />}
                              onClick={() => navigate(`/customer-accounts/cash-qr?customerId=${encodeURIComponent(row.customerId || row._id)}`)}
                            >
                              Cash QR
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCustomers}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              sx={{ borderTop: '1px solid #e2e8f0' }}
            />
          </>
        )}
      </TableContainer>

      {/* Customer profile synopsis dialog (eye buttons open the individual report pages) */}
      <CustomerProfileDialog
        open={profileModalOpen}
        onClose={() => setProfileModalOpen(false)}
        customer={customer}
        canViewSensitive={canViewSensitive}
      />
    </Box>
  );
};

export default CustomerSynopsisReport;
